// Phase 6 / C2-S — latitude × |dR| scatter, log-Y.
//
// C2-S: the point layer moved from ~14587 individual <circle>+<title> SVG nodes
// to a hybrid canvas renderer, because the SVG point subtree caused a multi-
// second main-thread rendering stall on fast scroll (proven in the headed-GPU
// benchmark: SVG rAF p95 ≈ 2143 ms vs canvas ≈ 18 ms). Architecture:
//   * axes / grid / tick labels / legend / mid-latitude bands stay SVG/DOM;
//   * ALL points draw on a base <canvas> (redrawn only on data / filter / size /
//     colour change — never on hover, status poll, or a light parent re-render);
//   * the hovered/selected point draws on a separate overlay <canvas>;
//   * a viewBox-space spatial grid answers pointer/touch hit-tests in O(local),
//     never scanning all points;
//   * one pointer handler; tooltip fields + click→/job/<id> preserved.
// No downsampling / binning / aggregation — every point is drawn, same
// coordinates / colours / radius / opacity as the SVG (see scatterGeometry).

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AnalyticsScatterPoint } from '../../api/types';
import { formatConfig } from './formatConfig';
import {
  HEIGHT,
  LEGEND_KEYS,
  PAD,
  POINT_OPACITY,
  POINT_RADIUS,
  SYSTEM_COLORS,
  WIDTH,
  buildGrid,
  computeYBounds,
  configColor,
  nearest,
  projectX,
  projectY,
} from './scatterGeometry';

const X_TICKS = [-90, -45, 0, 45, 90];
const Y_DECADES = [0.1, 1, 10, 100];
const LAT_BANDS: ReadonlyArray<readonly [number, number, string]> = [
  [-55, -35, 'lat-band-south'],
  [35, 55, 'lat-band-north'],
];

/** Hit radius in viewBox units. Touch gets a larger target than the mouse. */
const MOUSE_HIT_R = 6;
const TOUCH_HIT_R = 12;
/** Pointer travel (CSS px) above which a press is a scroll/drag, not a tap. */
const TAP_SLOP = 8;

interface HoverState {
  point: AnalyticsScatterPoint;
  /** viewBox-space coordinates of the point (0..700 / 0..360). */
  x: number;
  y: number;
}

interface Size {
  w: number;
  h: number;
  dpr: number;
}

function themeVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

function ScatterLatitudeResidualImpl({ points }: { points: AnalyticsScatterPoint[] }) {
  const navigate = useNavigate();
  const [hiddenConfigs, setHiddenConfigs] = useState<Set<string>>(() => new Set());
  const [hover, setHover] = useState<HoverState | null>(null);
  const [size, setSize] = useState<Size>({ w: 0, h: 0, dpr: 1 });

  const wrapRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const downRef = useRef<{ x: number; y: number; type: string } | null>(null);

  // Axis bounds from the FULL point set (toggling configs must not rescale Y).
  const { yMin, yMax } = useMemo(() => computeYBounds(points), [points]);

  // Projected pixels (viewBox space) + spatial grid — rebuilt ONLY on
  // data / filter / bounds change, never on hover or a light parent re-render.
  const { pix, grid } = useMemo(() => {
    const pix: { x: number; y: number; p: AnalyticsScatterPoint }[] = [];
    for (const p of points) {
      if (hiddenConfigs.has(p.config)) continue;
      pix.push({ x: projectX(p.latitude_deg), y: projectY(p.dr_3d_mm, yMin, yMax), p });
    }
    return { pix, grid: buildGrid(pix) };
  }, [points, hiddenConfigs, yMin, yMax]);

  // Track CSS size + DPR (ResizeObserver) so the backing store stays crisp.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const update = () => {
      const w = wrap.clientWidth;
      setSize({ w, h: w * (HEIGHT / WIDTH), dpr: window.devicePixelRatio || 1 });
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // Base layer — every visible point. Redraws only when pix or size change.
  useEffect(() => {
    const canvas = baseRef.current;
    if (!canvas || size.w === 0) return;
    canvas.width = Math.round(size.w * size.dpr);
    canvas.height = Math.round(size.h * size.dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform((size.w * size.dpr) / WIDTH, 0, 0, (size.h * size.dpr) / HEIGHT, 0, 0);
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    const fallback = themeVar(canvas, '--ink-3', '#7c7965');
    ctx.globalAlpha = POINT_OPACITY;
    for (const q of pix) {
      ctx.beginPath();
      ctx.arc(q.x, q.y, POINT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = configColor(q.p.config, fallback);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }, [pix, size]);

  // Overlay layer — only the hovered/selected point. Never touches the base.
  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas || size.w === 0) return;
    canvas.width = Math.round(size.w * size.dpr);
    canvas.height = Math.round(size.h * size.dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform((size.w * size.dpr) / WIDTH, 0, 0, (size.h * size.dpr) / HEIGHT, 0, 0);
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    if (!hover) return;
    const fallback = themeVar(canvas, '--ink-3', '#7c7965');
    ctx.beginPath();
    ctx.arc(hover.x, hover.y, POINT_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = configColor(hover.point.config, fallback);
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = themeVar(canvas, '--ink', '#1a1a1a');
    ctx.beginPath();
    ctx.arc(hover.x, hover.y, POINT_RADIUS + 2.5, 0, Math.PI * 2);
    ctx.stroke();
  }, [hover, size]);

  // Pointer hit-test — one handler, spatial grid, no full scan.
  const pick = useCallback(
    (clientX: number, clientY: number, touch: boolean): HoverState | null => {
      const wrap = wrapRef.current;
      if (!wrap || size.w === 0) return null;
      const rect = wrap.getBoundingClientRect();
      const vx = ((clientX - rect.left) / size.w) * WIDTH;
      const vy = ((clientY - rect.top) / size.h) * HEIGHT;
      const idx = nearest(grid, vx, vy, touch ? TOUCH_HIT_R : MOUSE_HIT_R);
      if (idx == null) return null;
      const q = pix[idx]!;
      return { point: q.p, x: q.x, y: q.y };
    },
    [grid, pix, size],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') return; // touch surfaces on tap, below
      const next = pick(e.clientX, e.clientY, false);
      setHover((prev) => (prev?.point.job_id === next?.point.job_id ? prev : next));
    },
    [pick],
  );
  const onPointerLeave = useCallback(() => setHover(null), []);
  // Record the press so a scroll/fling (moved past the tap slop) is never taken
  // for a tap — this is what keeps a fling that starts directly over a point
  // from opening that point's Job.
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    downRef.current = { x: e.clientX, y: e.clientY, type: e.pointerType };
  }, []);
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      const d = downRef.current;
      downRef.current = null;
      if (d && Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > TAP_SLOP) {
        setHover(null); // it was a scroll/drag, not a tap
        return;
      }
      const touch = d?.type === 'touch';
      const hit = pick(e.clientX, e.clientY, touch);
      if (touch) setHover(hit); // touch tap → tooltip; navigation via its link (SVG-parity)
      else if (hit) navigate(`/job/${hit.point.job_id}`); // mouse click → open the Job
    },
    [pick, navigate],
  );
  const onActivate = useCallback(() => {
    if (hover) navigate(`/job/${hover.point.job_id}`);
  }, [hover, navigate]);

  const toggleConfig = useCallback((cfg: string) => {
    setHiddenConfigs((prev) => {
      const next = new Set(prev);
      if (next.has(cfg)) next.delete(cfg);
      else next.add(cfg);
      return next;
    });
  }, []);

  if (points.length === 0) {
    return (
      <div className="scatter-section">
        <div className="section-title">точность по широтам</div>
        <div className="analytics-empty">нет данных по выбранным фильтрам</div>
      </div>
    );
  }

  const yLog = (dr: number) => Math.log10(Math.max(dr, 0.1));
  const scatterLabel = `Диаграмма рассеяния широта × |dR| (log). Видимо ${pix.length} из ${points.length} точек.`;

  return (
    <div className="scatter-section">
      <div className="section-title">точность по широтам (log scale Y)</div>

      <div
        className="scatter-svg-wrap"
        ref={wrapRef}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        onPointerDown={onPointerDown}
        onClick={onClick}
      >
        {/* Axes / grid / labels / bands — SVG (unchanged); points are on canvas. */}
        <svg
          className="scatter-svg"
          width={WIDTH}
          height={HEIGHT}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={scatterLabel}
          data-testid="scatter-svg"
        >
          {LAT_BANDS.map(([lo, hi, testId]) => (
            <rect
              key={testId}
              data-testid={testId}
              x={projectX(lo)}
              y={PAD.top}
              width={projectX(hi) - projectX(lo)}
              height={HEIGHT - PAD.top - PAD.bottom}
              fill="var(--ink-3)"
              fillOpacity={0.06}
            />
          ))}
          <text x={projectX(45)} y={PAD.top + 12} textAnchor="middle" fill="var(--ink-3)" fontSize="9" fontFamily="var(--font-mono, monospace)" letterSpacing="0.08em">СРЕДНИЕ ШИРОТЫ</text>
          <text x={projectX(-45)} y={PAD.top + 12} textAnchor="middle" fill="var(--ink-3)" fontSize="9" fontFamily="var(--font-mono, monospace)" letterSpacing="0.08em">СРЕДНИЕ ШИРОТЫ</text>

          {X_TICKS.map((lat) => (
            <g key={`x-${lat}`}>
              <line x1={projectX(lat)} y1={PAD.top} x2={projectX(lat)} y2={HEIGHT - PAD.bottom} stroke="var(--line-soft)" strokeWidth="0.5" />
              <text x={projectX(lat)} y={HEIGHT - PAD.bottom + 16} textAnchor="middle" fill="var(--ink-3)" fontSize="11" fontFamily="var(--font-mono, monospace)">{lat}°</text>
            </g>
          ))}

          {Y_DECADES.filter((d) => yLog(d) >= yMin && yLog(d) <= yMax).map((dr) => (
            <g key={`y-${dr}`}>
              <line x1={PAD.left} y1={projectY(dr, yMin, yMax)} x2={WIDTH - PAD.right} y2={projectY(dr, yMin, yMax)} stroke="var(--line-soft)" strokeWidth="0.5" strokeDasharray="2,2" />
              <text x={PAD.left - 6} y={projectY(dr, yMin, yMax) + 4} textAnchor="end" fill="var(--ink-3)" fontSize="11" fontFamily="var(--font-mono, monospace)">{dr < 1 ? dr : dr.toFixed(0)} мм</text>
            </g>
          ))}
        </svg>

        <canvas ref={baseRef} className="scatter-canvas scatter-canvas-base" data-testid="scatter-canvas" aria-hidden="true" />
        <canvas ref={overlayRef} className="scatter-canvas scatter-canvas-overlay" aria-hidden="true" />

        {hover && <ScatterTooltip hover={hover} size={size} onNavigate={onActivate} onClose={() => setHover(null)} />}
      </div>

      <div className="scatter-legend">
        {LEGEND_KEYS.map((sys) => {
          const hidden = hiddenConfigs.has(sys);
          return (
            <button
              key={sys}
              type="button"
              className={`legend-item legend-toggle ${hidden ? 'hidden' : ''}`}
              onClick={() => toggleConfig(sys)}
              aria-pressed={!hidden}
              data-testid={`legend-toggle-${sys}`}
            >
              <span className="legend-dot" style={{ background: SYSTEM_COLORS[sys] }} />
              <span className="legend-label">{formatConfig(sys)}</span>
            </button>
          );
        })}
      </div>

      {/* Accessibility — the canvas points are aria-hidden; this live region
          voices the selected point + a text summary of the plot. */}
      <div className="sr-only" aria-live="polite" data-testid="scatter-a11y">
        {hover
          ? `Выбрана точка: ${hover.point.station}, ${formatConfig(hover.point.config)}, |dR| ${hover.point.dr_3d_mm.toFixed(1)} мм, широта ${hover.point.latitude_deg.toFixed(1)}°`
          : scatterLabel}
      </div>
    </div>
  );
}

function ScatterTooltip({
  hover,
  size,
  onNavigate,
  onClose,
}: {
  hover: HoverState;
  size: Size;
  onNavigate: () => void;
  onClose: () => void;
}) {
  const { point, x, y } = hover;
  // Convert viewBox coords → on-screen px for placement (the plot is scaled to
  // the wrap width). Keep the tooltip inside the wrap.
  const px = size.w > 0 ? (x / WIDTH) * size.w : x;
  const py = size.h > 0 ? (y / HEIGHT) * size.h : y;
  const style: React.CSSProperties = {
    left: Math.max(0, Math.min(px + 10, Math.max(0, size.w - 190))),
    top: Math.max(0, py - 64),
  };
  return (
    <div className="scatter-tooltip" role="dialog" data-testid="scatter-tooltip" style={style} onMouseLeave={onClose}>
      <div className="scatter-tooltip-head">{point.station} · {formatConfig(point.config)}</div>
      <div className="scatter-tooltip-meta">{point.obs_date} · {point.tectonic_model}</div>
      <div className="scatter-tooltip-value">|dR| = {point.dr_3d_mm.toFixed(1)} мм · широта {point.latitude_deg.toFixed(1)}°</div>
      <button
        type="button"
        className="scatter-tooltip-link"
        onClick={(e) => {
          e.stopPropagation();
          onNavigate();
        }}
      >
        → карточка задачи
      </button>
    </div>
  );
}

// C2-S — memoized: `points` is stable via React Query structuralSharing, so a
// parent re-render (5s status poll / light query settle) never re-runs this
// component and never redraws the base canvas.
export const ScatterLatitudeResidual = memo(ScatterLatitudeResidualImpl);
